import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { axiosInstance } from "../../../../utils/axios";

import {
  Avatar as MuiAvatar,
  Box,
  Button,
  Divider as MuiDivider,
  Grid,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CloudUpload as MuiCloudUpload } from "@mui/icons-material";
import { spacing } from "@mui/system";
const CloudUpload = styled(MuiCloudUpload)(spacing);
const CenteredContent = styled.div`
  text-align: left;
`;
const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
  margin-bottom: 10px;
`;
const BigAvatar = styled(Avatar)`
  width: 120px;
  height: 120px;
  //   margin: 0 auto ${(props) => props.theme.spacing(2)};
`;
const Divider = styled(MuiDivider)(spacing);

function EventBranding({ eventId }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [eventName, setEventName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch current event logo on component mount
  useEffect(() => {
    if (eventId) {
      fetchEventLogo();
    }
  }, [eventId]);

  const fetchEventLogo = async () => {
    try {
      console.log("🔄 Fetching event logo for eventId:", eventId);
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEvent($id: ID!) {
            getEvent(id: $id) {
              id
              name
              logo
            }
          }
        `,
        variables: { id: eventId },
      });

      console.log("✅ GetEvent response:", response);

      // Check both possible response structures due to axios interceptor
      const eventData = response.getEvent || response.data?.getEvent;

      if (eventData) {
        console.log("📋 Event data:", eventData);
        setEventName(eventData.name || "Event");
        if (eventData.logo) {
          console.log("🖼️ Setting logo URL:", eventData.logo);
          setLogoUrl(eventData.logo);
        } else {
          console.log("⚠️ No logo found in event data");
          setLogoUrl(null);
        }
      } else {
        console.log("❌ No event data in response");
        console.log("📋 Full response structure:", response);
      }
    } catch (error) {
      console.error("Error fetching event logo:", error);
    }
  };

  // Generate initials from event name
  const getEventInitials = (name) => {
    if (!name) return "EV";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setAlert({
        open: true,
        message: "File size must be less than 2MB",
        severity: "error",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setAlert({
        open: true,
        message: "Please select a valid image file",
        severity: "error",
      });
      return;
    }

    try {
      setUploading(true);

      console.log("🔄 Starting GraphQL logo upload for eventId:", eventId);
      console.log("🔄 File details:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      // Convert file to base64 (same as Profile.js)
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation UploadEventLogo($eventId: ID!, $logoData: String!) {
            uploadEventLogo(eventId: $eventId, logoData: $logoData) {
              success
              logoUrl
              message
            }
          }
        `,
        variables: {
          eventId: eventId,
          logoData: base64Image,
        },
      });

      console.log("✅ GraphQL Upload response:", response);

      if (
        response.data?.uploadEventLogo?.success &&
        response.data?.uploadEventLogo?.logoUrl
      ) {
        setLogoUrl(response.data.uploadEventLogo.logoUrl);
        setAlert({
          open: true,
          message:
            response.data.uploadEventLogo.message ||
            "Event logo uploaded successfully!",
          severity: "success",
        });
      } else {
        throw new Error(
          response.data?.uploadEventLogo?.message ||
            "Upload failed - no logo URL returned"
        );
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to upload logo",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <React.Fragment>
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Event Branding
          </Typography>
          <Typography>
            Define the design, theme and branding for your event
          </Typography>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Box style={{ marginTop: "15px", marginBottom: "15px" }}>
        <Grid container mb={4}>
          <Grid md={7} style={{ marginRight: "10px" }}>
            <Grid item md={12} mb={2}>
              <Typography variant="h5" gutterBottom display="inline">
                Event logo
              </Typography>
              <br />
              <Typography>
                Add your own brand logo to increase your brand engagement
              </Typography>
            </Grid>
            <CenteredContent>
              <BigAvatar
                alt="Event Logo"
                src={logoUrl}
                sx={{
                  backgroundColor: logoUrl ? "transparent" : "primary.main",
                  fontSize: "2rem",
                  fontWeight: "bold",
                }}
              >
                {!logoUrl && getEventInitials(eventName)}
              </BigAvatar>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="raised-button-file"
                type="file"
                onChange={handleLogoUpload}
                disabled={uploading}
              />
              <label htmlFor="raised-button-file">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CloudUpload mr={2} /> Upload Logo
                    </>
                  )}
                </Button>
                <Typography variant="caption" display="block" gutterBottom>
                  Recommended format JPG, SVG or PNG | Up to 2 MB
                </Typography>
              </label>
            </CenteredContent>
          </Grid>
          <Grid md={4}>
            <Grid item md={12}>
              <img src="/static/img/others/EventBranding.png" alt="" />
            </Grid>
            <Grid item md={12}>
              <Button variant="contained" color="primary">
                Try Now
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default EventBranding;
